<template>
  <div class="page-body position-relative">

    <!-- FORM LOADING OVERLAY -->
    <div v-if="formLoading" class="section-loading">
      <v-progress-circular
          :size="70"
          :width="7"
          color="primary"
          indeterminate
      />
    </div>

    <!-- ===== TOGGLE HEADER ===== -->
    <div
        class="report-toggle"
        :class="{ expanded: isOpen }"
        @click="toggle"
    >
      <span class="title">Create New Report</span>
      <span class="arrow" :class="{ open: isOpen }">
        <svg width="16" height="16" viewBox="0 0 24 24">
          <path
              d="M7 10l5 5 5-5"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
          />
        </svg>
      </span>
    </div>

    <!-- ===== CONTENT ===== -->
    <transition name="collapse">
      <div v-if="isOpen" class="content">

        <!-- Brand -->
        <div class="mb-4">
          <BrandDropdown
              v-model="brandSelected"
              @setBrand="setBrand"
              @setBrands="setBrands"
              :required="true"
          />
          <div v-if="error.errorBrand" class="text-error">
            {{ error.errorBrand.message || 'กรุณาเลือก' }}
          </div>
        </div>

        <!-- Report Type -->
        <div class="mb-4">
          <label for="reportType" required>Report Type</label>
          <multiselect
              v-model="form.reportType"
              :options="reportTypeOptions"
              track-by="value"
              label="label"
              :allow-empty="false"
              @input="error.errorReportType = null"
          />
          <div v-if="error.errorReportType" class="text-error">
            {{ error.errorReportType.message || 'กรุณาเลือก' }}
          </div>
        </div>

        <!-- Year / Month -->
        <div class="row mb-4">
          <div class="col-md-6">
            <label for="month" required>Month</label>
            <multiselect
                v-model="monthSelected"
                :options="monthsWithDisabled"
                label="text"
                track-by="id"
                :allow-empty="false"
            />
            <div v-if="error.errorMonth" class="text-error">
              {{ error.errorMonth.message || 'กรุณาเลือก' }}
            </div>
          </div>

          <div class="col-md-6">
            <label for="year" required>Year</label>
            <multiselect
                v-model="yearSelected"
                :options="years"
                :allow-empty="false"
            />
            <div v-if="error.errorYear" class="text-error">
              {{ error.errorYear.message || 'กรุณาเลือก' }}
            </div>
          </div>
        </div>


        <div class="row mb-4">
          <!-- Price Type -->
          <div class="col-md-6">
            <label for="priceType">Price Type <span class="required">*</span></label>

            <div class="form-check">
              <input
                  class="form-check-input"
                  type="radio"
                  value="tag"
                  v-model="form.priceType"
                  id="tagPrice"
              />
              <label class="form-check-label" for="tagPrice">
                Tag Price
              </label>
            </div>

            <div class="form-check">
              <input
                  class="form-check-input"
                  type="radio"
                  value="net"
                  v-model="form.priceType"
                  id="netPrice"
              />
              <label class="form-check-label" for="netPrice">
                Net Price
              </label>
            </div>
          </div>

          <!-- Include V -->
          <div class="col-md-6">
            <div class="form-check">
              <input
                  class="form-check-input"
                  type="checkbox"
                  v-model="form.includeV"
                  id="includeV"
              />
              <label class="form-check-label" for="includeV">
                Include V
              </label>
            </div>
          </div>
        </div>

        <!-- Member Type -->
        <div class="mb-4">
          <label for="memberTypes"> Member Type</label>

          <multiselect
              v-model="form.memberTypes"
              :options="memberOptions"
              :multiple="true"
              :close-on-select="false"
              :clear-on-select="false"
              :preserve-search="true"
              placeholder="Select Member Type"
              track-by="value"
              label="label"
          />
        </div>

        <!-- Store -->
        <div class="mb-4">
          <label for="store">Store</label>

          <treeselect
              v-model="form.storeCodes"
              :multiple="true"
              :options="storeOptions"
              :clearable="true"
              :searchable="true"
              :close-on-select="false"
              :value-consists-of="'LEAF_PRIORITY'"
              :max-height="200"
              placeholder="Select Store"
              :disabled="!brandSelected || isLoadingStore"
          />

          <div v-if="error.errorStoreCodes" class="text-error">
            {{ error.errorStoreCodes.message || 'กรุณาเลือก' }}
          </div>
        </div>

        <div v-if="isLoadingStore" class="d-flex align-center mt-2">
          <v-progress-circular
              indeterminate
              size="20"
              width="2"
              color="primary"
          />
          <span class="ml-2 text-muted">Loading stores...</span>
        </div>

        <!-- Product Categories -->
        <div
            v-for="(cat, idx) in productCategories"
            :key="cat.level"
            class="mb-4"
        >
          <label for="productCategories">Product Category Level {{ cat.level }}</label>
          <multiselect
              v-model="cat.selected"
              :options="cat.options"
              :multiple="true"
              track-by="id"
              label="text"
              :close-on-select="false"
              :clear-on-select="false"
              :preserve-search="true"
              @open="onCategoryOpen(idx)"
              @input="onSelectCategory(idx)"
          >
            <template #beforeList>
              <div v-if="cat.loading" class="d-flex align-center px-3 py-2">
                <v-progress-circular
                    indeterminate
                    size="16"
                    width="2"
                    color="primary"
                />
                <span class="ml-2">Loading...</span>
              </div>
            </template>
            <template #option="{ option }">
              <span :class="{ 'text-muted': cat.loading }">
                {{ option.text }}
              </span>
            </template>
          </multiselect>


        </div>

        <!-- Buttons -->
        <div class="row">
          <div class="col-md-6 p-2">
            <button class="btn btn-outline-secondary w-100" @click="onCancel">
              Reset
            </button>
          </div>
          <div class="col-md-6 p-2">
          <button class="btn btn-outline-primary w-100" :disabled="formLoading"
                  @click="createReport">
            Create Report
          </button>
        </div>
        </div>

      </div>
    </transition>
    <v-snackbar
      v-model="snackbar"
      :timeout="2000"
      :color="snackbarColor"
      top
      class="snackbar-center"
  >
    {{ snackbarMessage }}
  </v-snackbar>
  </div>
</template>

<script>
import BrandDropdown from "components/Booking/BrandDropdown";
import Multiselect from "vue-multiselect";
import moment from "moment";
import StoresByRetailerService from "services/api/storesByRetailer.service";
import ProductService from "services/api/product.service";
import ListSurveyReport from "@/views/Reports/Survey/ListSurveyReport.vue";
import ReportService from "services/api/report.service";
import monthYearMixin from "@/utils/monthYearMixin";

export default {
  name: "CreateNewReport",
  mixins: [monthYearMixin],
  components: {
    ListSurveyReport,
    BrandDropdown,
    Multiselect,
  },
  data() {
    return {
      isOpen: false,
      isMemberOpen: false,
      pageLoading: false,
      formLoading: false,
      // Brand
      brandSelected: null,
      brandItems: 0,

      // Year / Month
      yearSelected: moment().format("YYYY"),
      years: [],
      monthSelected: null,
      months: [],

      storeOptions: [],
      isLoadingStore: false,

      // Form
      form: {
        priceType: "tag",
        includeV: false,
        memberTypes: [],
        reportType: null,
        storeCodes: [],
      },

      memberOptions: [
        {value: "New", label: "New"},
        {value: "Existing", label: "Existing"},
        {value: "Reactivated", label: "Reactivated"},
      ],

      reportTypeOptions: [
        {
          value: "MTD",
          label: "MTD (Month to Date)",
        },
        {
          value: "YTD",
          label: "YTD (Year to Date)",
        },
        {
          value: "RTD",
          label: "RTD (12 Month Rolling)",
        },
      ],

      productCat1: [],
      productCategory1: null,

      productCategories: [
        { level: 1, selected: [], options: [], loaded: false, loading: false },
        { level: 2, selected: [], options: [], loaded: false, loading: false },
        { level: 3, selected: [], options: [], loaded: false, loading: false },
        { level: 4, selected: [], options: [], loaded: false, loading: false },
        { level: 5, selected: [], options: [], loaded: false, loading: false },
      ],

      selectedBrandId: null,

      error: {
        errorBrand: null,
        errorReportType: null,
        errorYear: null,
        errorMonth: null,
        errorStoreCodes: null,
      },

      snackbar: false,
      snackbarMessage: "",
      snackbarColor: "success",
    };
  },

  mounted() {
    this.formLoading = true;

    this.setYears();
    this.setMonths();

    this.$nextTick(() => {
      this.formLoading = false;
    });
  },

  methods: {
    toggle() {
      this.isOpen = !this.isOpen;
    },

    async createReport() {
      try {
        this.formLoading = true;
        this.clearErrors();

        const body = this.buildRequestBody();
        const res = await ReportService.createAgeGroupReport(body);

        if (res.data?.status === "success") {
          this.showSnackbar("Create report สำเร็จ", "success");
          this.$emit("created");
          this.isOpen = false;
        }
      } catch (err) {
        const errRes = err?.response;
        const errorData = errRes?.data;

        if (errorData?.errors?.length) {
          this.setErrorText(errorData.errors);
          return;
        }
        this.showSnackbar(errorData?.message || "ไม่สามารถสร้างรายงานได้", "danger");
      } finally {
        this.formLoading = false;
      }
    },

    buildRequestBody() {
      const body = {
        brandCode: this.brandSelected?.id ?? null,
        focusMonth: this.monthSelected?.id ?? null,
        focusYear: this.yearSelected
            ? Number.parseInt(this.yearSelected, 10)
            : null,
        reportPeriodType: this.form.reportType?.value ?? null,
        priceType: this.form.priceType?.toUpperCase() ?? null,
        memberTypes: (this.form.memberTypes || []).map(m => m.value),
        storeCodes: this.form.storeCodes || [],
        includeV: this.form.includeV,
      };

      this.productCategories.forEach((cat, idx) => {
        if (cat.selected?.length > 0) {
          body[`categoryLevel${idx + 1}Codes`] =
              cat.selected.map(sel => sel.id);
        }
      });

      return body;
    },

    async setBrand(brand) {
      this.error.errorBrand = null;
      this.error.errorStoreCodes = null;

      this.brandSelected = brand;

      this.storeOptions = [];
      this.form.storeCodes = [];
      this.isLoadingStore = false;

      this.productCategories.forEach(cat => {
        cat.selected = [];
        cat.options = [];
        cat.loaded = false;
        cat.loading = false;
      });

      await this.getStoresByBrand();
    },

    setBrands(brands) {
      this.brandItems = brands.length;

      if (brands.length === 1) {
        this.brandSelected = brands[0];
      }

      this.isLoading = false;
    },

    setYears() {
      const currentYear = Number.parseInt(moment().format("YYYY"));
      for (let i = currentYear; i >= 1950; i--) {
        this.years.push(i.toString());
      }
    },
    setMonths() {
      moment.months().forEach((month, index) => {
        this.months.push({
          id: index + 1,
          text: month,
        });
      });

      const prev = moment().subtract(1, "month");
      this.yearSelected = prev.year().toString();
      this.monthSelected = this.months.find(
          (m) => m.id === prev.month() + 1
      );
    },

    async getStoresByBrand() {
      if (!this.brandSelected?.id) {
        this.storeOptions = [];
        return;
      }

      this.isLoadingStore = true;

      try {
        const params = {brandCode: this.brandSelected.id, showAll: true};
        const res = await StoresByRetailerService.getListStoresByRetailer(params);

        const stores = res.data.storesByRetailer || [];

        this.storeOptions = stores.map((s) => ({
          id: s.retailerName,
          label: s.retailerName,
          children: s.stores.map((d) => ({
            id: d.storeCode,
            label: d.storeName,
          })),
        }));
      } catch (err) {
        console.error("getStoresByBrand error:", err);
      } finally {
        this.isLoadingStore = false;
      }
    },

    async onCategoryOpen(levelIndex) {
      if (!this.brandSelected?.id) return;

      const cat = this.productCategories[levelIndex];
      if (cat.loading || cat.loaded) return;

      cat.loading = true;
      cat.options = [];

      try {
        const params = {
          brandCode: this.brandSelected.id,
          level: levelIndex + 1,
        };

        for (let i = 0; i < levelIndex; i++) {
          const prev = this.productCategories[i];
          if (prev.selected.length > 0) {
            params[`categoryLevel${i + 1}Codes`] =
                prev.selected.map(s => s.id);
          }
        }

        const res = await ProductService.getProductCategories(params);

        cat.options = (res.data.categories || []).map(c => ({
          id: c.code,
          text: c.name,
        }));

        cat.loaded = true;
      } finally {
        cat.loading = false;
      }
    },

    onSelectCategory(levelIndex) {
      for (let i = levelIndex + 1; i < this.productCategories.length; i++) {
        this.productCategories[i].selected = [];
        this.productCategories[i].options = [];
        this.productCategories[i].loaded = false;
      }
    },

    setErrorText(errors = []) {
      this.clearErrors();

      errors.forEach(e => {
        switch (e.fieldName) {
          case "brandCode":
            this.error.errorBrand = e;
            break;

          case "reportPeriodType":
            this.error.errorReportType = e;
            break;

          case "focusYear":
            this.error.errorYear = e;
            break;

          case "focusMonth":
            this.error.errorMonth = e;
            break;

          case "storeCodes":
            this.error.errorStoreCodes = e;
            break;
        }
      });
    },

    clearErrors() {
      this.error = {
        errorBrand: null,
        errorReportType: null,
        errorYear: null,
        errorMonth: null,
        errorStoreCodes: null,
      };
    },

    showSnackbar(message, color = "success") {
      this.snackbarMessage = message;
      this.snackbarColor = color;
      this.snackbar = true;
    },

    onCancel() {
      this.resetForm();
    },

    resetForm() {
      // ---------- Brand ----------
      this.brandSelected = null;

      // ---------- Form ----------
      this.form.reportType = null;
      this.form.storeCodes = [];
      this.form.memberTypes = [];
      this.form.priceType = "tag";
      this.form.includeV = false;

      // ---------- Year / Month ----------
      const prev = moment().subtract(1, "month");
      this.yearSelected = prev.year().toString();
      this.monthSelected = this.months.find(
          m => m.id === prev.month() + 1
      );

      // ---------- Stores ----------
      this.storeOptions = [];
      this.isLoadingStore = false;

      this.productCategories.forEach(cat => {
        cat.selected = [];
        cat.options = [];
        cat.loaded = false;
        cat.loading = false;
      });

      // ---------- Errors ----------
      this.clearErrors();
    }

  },
  watch: {
    yearSelected(newVal) {
      const y = Number.parseInt(newVal, 10);

      if (y > this.currentYearNum) {
        this.monthSelected = null;
        return;
      }

      if (y === this.currentYearNum) {
        const prevMonth = this.currentMonthNum - 1;
        this.monthSelected =
            prevMonth >= 1
                ? this.months.find((m) => m.id === prevMonth)
                : null;
        return;
      }

      if (!this.monthSelected) {
        this.monthSelected = this.months.find((m) => m.id === 12);
      }
    },
  },

};

</script>

<style scoped>
label[required]::after {
  content: "*";
  color: red;
  margin-left: 5px;
}

.report-toggle {
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 44px;
  padding: 0 16px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  cursor: pointer;
  background: #fff;
}

.report-toggle:hover {
  background: #fafafa;
}

.title {
  font-size: 14px;
  font-weight: 500;
}

.arrow {
  transition: transform 0.2s ease;
}

.arrow.open {
  transform: rotate(180deg);
}

.content {
  margin-top: 12px;
  padding: 16px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background: #fafafa;
}

.text-error {
  color: #dc3545;
  font-size: 0.875rem;
  margin-top: 4px;
}

.page-body {
  padding: 16px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
}

.section-loading {
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.6);
  z-index: 20;
  display: flex;
  justify-content: center;
  align-items: center;
}
</style>
